import {
  IconBulb,
  IconChevronLeft,
  IconPencil,
  IconSearch,
  IconStethoscope,
  type TablerIcon,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { cn } from "../../../lib/utils";

/**
 * The four conversation starters offered on the JuisciGPT landing. The id is
 * the only thing that crosses the component boundary (the page owns which
 * category is open, so the vertical list and the composer-adjacent scroller
 * stay in sync across a focus change).
 */
export type PrePromptCategoryId = "search" | "understand" | "decide" | "write";

/** Gap between two consecutive items' entrance animations. */
const STAGGER_STEP_MS = 45;

type PrePromptCategory = {
  id: PrePromptCategoryId;
  label: string;
  Icon: TablerIcon;
  /**
   * `prompt` is the STUB inserted into the composer — a sentence opening the
   * user completes with their own subject. It never ends with a space: the
   * caller appends the separator when it seeds the input.
   */
  prompts: { label: string; prompt: string }[];
};

/**
 * JuisciGPT landing starters: a two-level menu. Level 1 is an intent (with a
 * Tabler icon), level 2 refines it into a concrete pre-prompt that pre-fills
 * the composer. Nothing is sent — the user completes the sentence and submits.
 *
 * The open category lives in the parent (see {@link PrePromptCategoryId}) so
 * the `column` list (landing, composer idle) and the `row` scroller (composer
 * focused, sitting on top of the input) render the same level: focusing the
 * input swaps one for the other and must not reset the drill-down.
 *
 * `inline` is the desktop landing (Figma "D3.explore.pour-vous"): the four
 * intents sit on one line of `.TabChip`s under the composer, and an open
 * intent turns into a list below it. There is no keyboard to dodge on a
 * desktop, so the one layout serves the composer idle and focused alike.
 */
export function GPTPrePrompts({
  layout,
  className,
  selectedCategoryId,
  onSelectCategory,
  onPickPrompt,
}: {
  layout: "column" | "row" | "inline";
  className: string;
  selectedCategoryId: PrePromptCategoryId | null;
  onSelectCategory: (categoryId: PrePromptCategoryId | null) => void;
  onPickPrompt: (prompt: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();

  // Built per render (never hoisted to module scope): every label AND every
  // stub is translated, and the stub is what the user reads back inside the
  // composer. `lng` is pinned to the app's active language so the copy matches
  // the language the answer will be written in.
  const categories: PrePromptCategory[] = [
    {
      id: "search",
      label: t("Search", { lng: activeLang }),
      Icon: IconSearch,
      prompts: [
        {
          label: t("Recent publications on a topic", { lng: activeLang }),
          prompt: t("What are the most recent publications about", {
            lng: activeLang,
          }),
        },
        {
          label: t("Current guidelines", { lng: activeLang }),
          prompt: t("What are the current guidelines for the management of", {
            lng: activeLang,
          }),
        },
        {
          label: t("Compare two strategies", { lng: activeLang }),
          prompt: t(
            "Compare the efficacy and the safety of these two strategies:",
            { lng: activeLang }
          ),
        },
        {
          label: t("Fact-check a claim", { lng: activeLang }),
          prompt: t("What does the scientific evidence say about this claim:", {
            lng: activeLang,
          }),
        },
        {
          label: t("State of the art on a topic", { lng: activeLang }),
          prompt: t(
            "Give me an evidence-based overview of the state of the art on",
            { lng: activeLang }
          ),
        },
      ],
    },
    {
      id: "understand",
      label: t("Understand", { lng: activeLang }),
      Icon: IconBulb,
      prompts: [
        {
          label: t("Explain a mechanism", { lng: activeLang }),
          prompt: t("Explain the mechanism of action of", { lng: activeLang }),
        },
        {
          label: t("Summarise a study", { lng: activeLang }),
          prompt: t("Summarise the key findings of this study:", {
            lng: activeLang,
          }),
        },
        {
          label: t("Assess a methodology", { lng: activeLang }),
          prompt: t(
            "Assess the methodology and the limitations of this study:",
            {
              lng: activeLang,
            }
          ),
        },
        {
          label: t("Explain the statistics", { lng: activeLang }),
          prompt: t(
            "Explain the statistical results of this study and what they mean in practice:",
            { lng: activeLang }
          ),
        },
        {
          label: t("Revise a topic", { lng: activeLang }),
          prompt: t("Help me revise the essentials I should know about", {
            lng: activeLang,
          }),
        },
      ],
    },
    {
      id: "decide",
      label: t("Decide", { lng: activeLang }),
      Icon: IconStethoscope,
      prompts: [
        {
          label: t("Build a differential diagnosis", { lng: activeLang }),
          prompt: t(
            "What differential diagnosis should I consider for a patient presenting with",
            { lng: activeLang }
          ),
        },
        {
          label: t("Plan a diagnostic workup", { lng: activeLang }),
          prompt: t("What diagnostic workup is recommended for", {
            lng: activeLang,
          }),
        },
        {
          label: t("Choose a first-line treatment", { lng: activeLang }),
          prompt: t("What is the recommended first-line treatment for", {
            lng: activeLang,
          }),
        },
        {
          label: t("Check dosing and interactions", { lng: activeLang }),
          prompt: t(
            "What are the recommended dosing, the interactions and the contraindications of",
            { lng: activeLang }
          ),
        },
        {
          label: t("Plan follow-up and monitoring", { lng: activeLang }),
          prompt: t("What follow-up and monitoring is recommended after", {
            lng: activeLang,
          }),
        },
      ],
    },
    {
      id: "write",
      label: t("Write", { lng: activeLang }),
      Icon: IconPencil,
      prompts: [
        {
          label: t("A literature review", { lng: activeLang }),
          prompt: t("Write a structured, referenced literature review on", {
            lng: activeLang,
          }),
        },
        {
          label: t("A presentation for my team", { lng: activeLang }),
          prompt: t("Help me prepare a presentation for my team on", {
            lng: activeLang,
          }),
        },
        {
          label: t("An explanation for a patient", { lng: activeLang }),
          prompt: t("Explain in simple words, for a patient,", {
            lng: activeLang,
          }),
        },
        {
          label: t("A scientific abstract", { lng: activeLang }),
          prompt: t("Help me write a scientific abstract on", {
            lng: activeLang,
          }),
        },
      ],
    },
  ];

  const openCategory =
    selectedCategoryId === null
      ? null
      : categories.find((category) => category.id === selectedCategoryId);
  if (openCategory === undefined)
    throw new Error(
      `should not happen: unknown pre-prompt category ${selectedCategoryId}`
    );

  const isRow = layout === "row";
  const isInlineIntents = layout === "inline" && openCategory === null;

  // Row only: keep the composer focused when the scroller sits above an open
  // keyboard. Blurring would unmount the scroller before the click dispatches
  // (swallowing the tap AND dismissing the keyboard). Synthetic mousedown is
  // suppressed during a touch-scroll, so swipe-to-scroll still works.
  function keepComposerFocused(event: React.MouseEvent<HTMLButtonElement>) {
    if (isRow) event.preventDefault();
  }

  // Row items are pills on a single scrolling line; column items are chips
  // stacked into a left-aligned list that hugs its own labels (Figma
  // ".TabChip"). Both sit on the same type ramp (`text-ui-body`, 15/20), so the
  // drill-down reads as one list changing level rather than two components —
  // only the container shape differs.
  // `shrink-0` in both: the column list is a flex child of a fixed-height half
  // (GPTNewChatPage) and would otherwise squash its rows instead of scrolling.
  const itemClassName = cn(
    "flex shrink-0 items-center border-none bg-transparent text-left font-dm-sans text-ui-body",
    "cursor-pointer transition-colors animate-stagger-in",
    isRow
      ? "select-none gap-2 whitespace-nowrap rounded-full border-[0.5px] border-solid border-ds-slate-200 bg-ds-white px-5 py-2.5 text-ds-slate-800 shadow-[0px_2px_8px_rgba(0,0,0,0.04)]"
      : // moss-600, not moss-500: 500 on cream sits under 4.5:1 and fails AA at
        // this size (Figma `color/moss/600`).
        [
          "w-fit max-w-full rounded-lg px-3 py-2.5 text-ds-moss-600",
          // The desktop `.TabChip` hovers to the DS black 8% and mint-950;
          // the phone's list keeps its slate wash.
          layout === "inline"
            ? "hover:bg-ds-black/8 hover:text-ds-mint-950"
            : "hover:bg-ds-slate-100",
          // The Button's ring (Figma ".TabChip" state=focus).
          layout === "inline" &&
            "focus-visible:shadow-focus-ring focus-visible:outline-none",
          // The desktop `.TabChip`: a 16px glyph tight against its label.
          isInlineIntents ? "gap-1" : "gap-3",
        ]
  );

  // Items slide up one after the other. The delay is per-position, so it must
  // be inline — there is no class for "the 4th item". Switching level swaps in
  // a differently-keyed set of buttons, so React mounts fresh nodes and the
  // whole cascade replays on every drill-down and every step back.
  function staggerStyle(position: number): React.CSSProperties {
    return { animationDelay: `${position * STAGGER_STEP_MS}ms` };
  }

  return (
    <div
      className={cn(
        isRow
          ? [
              "flex gap-2 overflow-x-auto overscroll-x-contain touch-pan-x",
              "[-ms-overflow-style:none] scrollbar-none [&::-webkit-scrollbar]:hidden",
            ]
          : isInlineIntents
            ? "flex w-full flex-wrap gap-2"
            : "flex w-full flex-col gap-1 md:max-w-2xl",
        className
      )}
      // Same reason as `keepComposerFocused`, for the swipe instead of the tap:
      // the mobile shell dismisses the keyboard on any `touchmove` reaching it
      // (AppLayout), and in row layout that blur unmounts this scroller under
      // the user's finger. Its drags belong to it, not to the page.
      onTouchMove={(e) => {
        e.stopPropagation();
      }}
    >
      {openCategory === null ? (
        categories.map((category, position) => (
          <button
            key={category.id}
            type="button"
            className={itemClassName}
            style={staggerStyle(position)}
            onMouseDown={keepComposerFocused}
            onClick={() => {
              onSelectCategory(category.id);
            }}
          >
            {/* size-5 is exactly the line box of the shared ramp (15/20), so an
                icon row and an icon-less refinement row are the same height in
                either layout. */}
            <category.Icon
              className={cn(
                "shrink-0",
                isInlineIntents ? "size-4" : "size-5",
                isRow
                  ? "text-ds-slate-500"
                  : // Inline, the glyph follows its label into the hover.
                    layout === "inline"
                    ? "text-current"
                    : "text-ds-moss-600"
              )}
              // The 16px glyph takes the DS sm stroke.
              stroke={isInlineIntents ? 1.25 : 1.5}
            />
            <span className="font-medium">{category.label}</span>
          </button>
        ))
      ) : (
        <>
          <button
            type="button"
            className={cn(itemClassName, "text-ds-slate-500")}
            style={staggerStyle(0)}
            onMouseDown={keepComposerFocused}
            onClick={() => {
              onSelectCategory(null);
            }}
          >
            {/* Sized like the category glyphs of its own layout, so stepping
                back does not shift the row it replaces. */}
            <IconChevronLeft
              className={cn("shrink-0", isRow ? "size-4" : "size-5")}
              stroke={1.5}
            />
            <span className="font-medium">{openCategory.label}</span>
          </button>
          {openCategory.prompts.map((item, position) => (
            <button
              key={item.label}
              type="button"
              className={itemClassName}
              // The back row occupies position 0 of this level's cascade.
              style={staggerStyle(position + 1)}
              onMouseDown={keepComposerFocused}
              onClick={() => {
                onPickPrompt(item.prompt);
              }}
            >
              <span>{item.label}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
